import { LooplayIframeAuth, DEFAULT_API_URL, resolveEnvApiUrl } from '@looplay/sdk';

function readGameKey() {
  const query = typeof location !== 'undefined' ? new URLSearchParams(location.search) : null;
  const q = query?.get('appId') || query?.get('gameKey');
  const envVal = (import.meta.env?.VITE_LOOPLAY_APP_ID || '').trim();
  return (q && q !== 'YOUR_APP_ID' ? q : '') || (envVal && envVal !== 'YOUR_APP_ID' ? envVal : '') || 'fa87a1f0-6c9d-468f-88ca-721237e17e05';
}

function readBaseUrl() {
  const query = typeof location !== 'undefined' ? new URLSearchParams(location.search) : null;
  const q = query?.get('apiUrl') || query?.get('baseUrl');
  const envBase = (
    import.meta.env?.VITE_LOOPLAY_API_URL ||
    import.meta.env?.VITE_LOOPLAY_BASE_URL ||
    import.meta.env?.VITE_API_URL ||
    ''
  ).trim();

  let candidate = q || envBase;
  if (!candidate) {
    try {
      candidate = resolveEnvApiUrl();
    } catch {
      candidate = null;
    }
  }
  return candidate || DEFAULT_API_URL;
}

function isTelegramMiniApp() {
  if (typeof window === 'undefined') return false;

  const params = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const flagNames = ['telegramMiniApp', 'telegram_mini_app'];
  const hasUrlFlag = flagNames.some((key) => params.has(key) || hashParams.has(key));

  if (hasUrlFlag) return true;
  if (window.parent === window) return false;

  try {
    return Boolean(window.parent?.Telegram?.WebApp);
  } catch {
    return false;
  }
}

class LooplayBridge {
  constructor() {
    this.auth = null;
    this.initPromise = null;
    this.matchId = null;
    this.activeRound = null;

    this._onSessionStartCallbacks = [];
    this._onWinCallbacks = [];
    this._onStopCallbacks = [];

    this.#initAuth();
  }

  get enabled() {
    return this.auth ? this.auth.canTrack() : false;
  }

  get sdk() {
    return this.auth;
  }

  get canRequestRewardedAd() {
    return isTelegramMiniApp() && Boolean(this.auth);
  }

  #initAuth() {
    if (this.auth) return this.auth;
    try {
      this.auth = new LooplayIframeAuth({
        appId: readGameKey(),
        apiUrl: readBaseUrl(),
        parentOrigin: import.meta.env?.VITE_PARENT_ORIGIN || undefined,
        autoPlaytime: {
          enabled: true,
          intervalMs: 30000,
          pauseOnHidden: true,
          pauseOnBlur: true,
        },
        autoRequestAds: {
          enabled: false,
        },
        onDebug: (message, details) => {
          if (details) {
            console.debug(`[Looplay] ${message}`, details);
          } else {
            console.debug(`[Looplay] ${message}`);
          }
        },
      });
    } catch (error) {
      console.warn('[Looplay] Failed to create LooplayIframeAuth:', error);
      this.auth = null;
    }
    return this.auth;
  }

  async init() {
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      if (!this.auth) this.#initAuth();
      if (!this.auth) return false;

      try {
        this.auth.init();
        this.auth.initLifecycleTracking();
        await this.auth.trackView();
        return true;
      } catch (error) {
        console.warn('[Looplay] SDK initialization failed; continuing in fallback mode:', error);
        return false;
      }
    })();

    return this.initPromise;
  }

  startSession(meta = {}) {
    const id = meta.matchId || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `match_${Date.now()}`);
    this.matchId = id;

    if (this.activeRound) {
      this.stop({ reason: 'abandoned' });
    }

    this.activeRound = {
      id,
      meta,
    };

    if (this.auth) {
      try {
        // Starts auto active playtime tracking and initializes session
        this.auth.startPlaySession();

        const payload = {
          game: 'gunny-pve',
          gameMode: 'PVE',
          ...meta,
        };

        void this.auth.emitGameEvent('GAME_STARTED', {
          value: Number(meta.level) || 1,
          payload,
        });
        void this.auth.emitGameEvent('LEVEL_STARTED', {
          value: Number(meta.level) || 1,
          payload,
        });
      } catch (error) {
        console.warn('[Looplay] startSession error:', error);
      }
    }

    this._onSessionStartCallbacks.forEach((cb) => {
      try {
        cb(meta);
      } catch (_) {}
    });

    return this.matchId;
  }

  tick() {
    // No-op: Active playtime tracking, focus/blur pause, and 30s periodic sync are handled automatically by @looplay/sdk
  }

  completeLevel({ won, score = 0, level = 1, mapId, difficulty, turns = 0 } = {}) {
    const id = this.matchId;
    const roundToComplete = this.activeRound;
    if (!id || !roundToComplete) return;

    this.matchId = null;
    this.activeRound = null;

    const duration = Math.max(1, this.auth ? this.auth.getPlayTimeSeconds() : 1);

    const common = {
      game: 'gunny-pve',
      matchId: id,
      matchDurationSeconds: duration,
      isCompleted: true,
      isWin: Boolean(won),
      score,
      level,
      mapId,
      difficulty,
      turns,
    };

    if (this.auth) {
      try {
        void this.auth.emitGameEvent(won ? 'LEVEL_COMPLETED' : 'LEVEL_FAILED', {
          value: won ? Math.max(1, Number(score) || 1) : 1,
          payload: common,
        });

        void this.auth.trackMatch(id, {
          durationSeconds: duration,
          isCompleted: true,
          isWin: Boolean(won),
        });

        // Completes play session: SDK auto-sends the exact tracked playtime
        void this.auth.completePlaySession();
      } catch (error) {
        console.warn('[Looplay] completeLevel error:', error);
      }
    }

    if (won) {
      this._onWinCallbacks.forEach((cb) => {
        try {
          cb(common);
        } catch (_) {}
      });
    } else {
      this._onStopCallbacks.forEach((cb) => {
        try {
          cb({ ...common, won: false });
        } catch (_) {}
      });
    }
  }

  stop(payload = {}) {
    const id = this.matchId;
    const roundToStop = this.activeRound;
    if (!id || !roundToStop) return;

    this.matchId = null;
    this.activeRound = null;

    const duration = Math.max(1, this.auth ? this.auth.getPlayTimeSeconds() : 1);

    if (this.auth) {
      try {
        void this.auth.trackMatch(id, {
          durationSeconds: duration,
          isCompleted: false,
          isWin: false,
        });

        void this.auth.completePlaySession();
      } catch (error) {
        console.warn('[Looplay] stop error:', error);
      }
    }

    this._onStopCallbacks.forEach((cb) => {
      try {
        cb(payload);
      } catch (_) {}
    });
  }

  pausePlay() {
    this.auth?.pausePlay();
  }

  resumePlay() {
    this.auth?.resumePlay();
  }

  getPlayTimeSeconds() {
    return this.auth ? this.auth.getPlayTimeSeconds() : 0;
  }

  isPlayTimePaused() {
    return this.auth ? this.auth.isPlayTimePaused() : false;
  }

  async requestRewardedAd() {
    if (!this.canRequestRewardedAd || !this.auth) return false;

    try {
      return await this.auth.requestAds({ timeoutMs: 30000 });
    } catch (error) {
      console.warn('[Looplay] Rewarded ad request failed:', error);
      return false;
    }
  }

  onSessionStart(cb) {
    if (typeof cb === 'function') this._onSessionStartCallbacks.push(cb);
  }

  onWin(cb) {
    if (typeof cb === 'function') this._onWinCallbacks.push(cb);
  }

  onStop(cb) {
    if (typeof cb === 'function') this._onStopCallbacks.push(cb);
  }
}

export const looplay = new LooplayBridge();
export default looplay;
